import { ambianceFileUrl } from '@/lib/api';
import type { ExperienceBlockDraft } from '@matter_extractor/shared';
import type { ReactNode } from 'react';
import { READER_TEST_IDS, type ReaderMode } from '../contracts';

export interface ImageBlockProps {
  readonly block: Extract<ExperienceBlockDraft, { type: 'image' }>;
  readonly mode: ReaderMode;
}

/**
 * The Image block (§9.2): a file of the media library, its caption, its width
 * and its alignment.
 *
 * The width and the alignment are the BLOCK's own tokens - `reader.css` spends
 * `data-width` and `data-align` on the frame - so nothing is sized here.
 *
 * ## An image without a file
 *
 * `assetId` is legitimately null: « un bloc image existe avant qu'un fichier
 * soit choisi, et un bloc vide est une page qui ne montre rien plutôt qu'une
 * version que personne ne peut enregistrer » (§9.2). So in the session and in
 * the Timeline's scene it draws NOTHING - a placeholder would be a thing the
 * person sees that the operator never put there. In `edit` it draws a dashed
 * slot, because a block one cannot click is a block one cannot fill.
 *
 * ## The caption is the figure's text, so the image is decorative
 *
 * §9.2 gives the block ONE text field, and a `<figcaption>` already names the
 * `<figure>` it sits in: repeating it in `alt` would have a screen reader read
 * the same sentence twice. Inventing a second field in the reader would be a
 * field nobody can edit, so `alt` stays empty - which is also the honest answer
 * for an image whose operator wrote no text at all.
 */
export function ImageBlock({ block, mode }: ImageBlockProps): ReactNode {
  if (block.assetId === null) {
    if (mode !== 'edit') return null;
    return (
      <p className="lecteur-bloc-vide" data-testid={READER_TEST_IDS.emptyAsset}>
        Aucune image choisie
      </p>
    );
  }

  return (
    <figure className="lecteur-image-figure">
      <img className="lecteur-image" src={ambianceFileUrl(block.assetId)} alt="" />
      {block.caption === '' ? null : (
        <figcaption className="lecteur-legende">{block.caption}</figcaption>
      )}
    </figure>
  );
}
